import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit2, Trash2, X, DollarSign } from 'lucide-react';
import { roomTypesApi, locationsApi } from '@/api/client';
import { formatCurrency } from '@medical-counsellor/shared';
import { toast } from '@/utils/toast';

const roomTypeSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    description: z.string().optional(),
    defaultRate: z.number().positive('Default rate must be positive'),
    markupPercentage: z.number().min(0).max(100).default(0),
    displayOrder: z.number().int().nonnegative().default(0),
});

type RoomTypeFormData = z.infer<typeof roomTypeSchema>;

export function RoomTypesPage() {
    const [selectedLocationId, setSelectedLocationId] = useState<string>('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRoomType, setEditingRoomType] = useState<any>(null);
    const queryClient = useQueryClient();

    const { data: locations = [] } = useQuery({
        queryKey: ['locations'],
        queryFn: locationsApi.list,
    });

    const { data: roomTypes = [], isLoading } = useQuery({
        queryKey: ['room-types', selectedLocationId],
        queryFn: () => roomTypesApi.list(selectedLocationId),
        enabled: !!selectedLocationId,
    });

    const createMutation = useMutation({
        mutationFn: (data: RoomTypeFormData & { locationId: string }) =>
            roomTypesApi.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['room-types'] });
            toast.success('Room type created successfully');
            setIsModalOpen(false);
            setEditingRoomType(null);
            reset({
                name: '',
                description: '',
                defaultRate: 0,
                markupPercentage: 0,
                displayOrder: 0,
            });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to create room type');
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<RoomTypeFormData> }) =>
            roomTypesApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['room-types'] });
            toast.success('Room type updated successfully');
            setIsModalOpen(false);
            setEditingRoomType(null);
            reset({
                name: '',
                description: '',
                defaultRate: 0,
                markupPercentage: 0,
                displayOrder: 0,
            });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to update room type');
        },
    });

    const deleteMutation = useMutation({
        mutationFn: roomTypesApi.delete,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['room-types'] });
            toast.success('Room type deleted successfully');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to delete room type');
        },
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<RoomTypeFormData>({
        resolver: zodResolver(roomTypeSchema),
    });

    const onSubmit = (data: RoomTypeFormData) => {
        if (editingRoomType) {
            updateMutation.mutate({ id: editingRoomType.id, data });
        } else {
            createMutation.mutate({ ...data, locationId: selectedLocationId });
        }
    };

    const handleEdit = (roomType: any) => {
        setEditingRoomType(roomType);
        reset(roomType);
        setIsModalOpen(true);
    };

    // Auto-select first location if available
    if (locations.length > 0 && !selectedLocationId) {
        setSelectedLocationId(locations[0].id);
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-900">Room Types</h1>
                <p className="text-slate-600">Manage room types and default rates per location</p>
            </div>

            {/* Location Selector */}
            <div className="card">
                <div className="card-body">
                    <label className="label">Select Location</label>
                    <select
                        className="input max-w-md"
                        value={selectedLocationId}
                        onChange={(e) => setSelectedLocationId(e.target.value)}
                    >
                        <option value="">Choose a location...</option>
                        {locations.map((location: any) => (
                            <option key={location.id} value={location.id}>
                                {location.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {selectedLocationId && (
                <div className="card">
                    <div className="card-header flex items-center justify-between">
                        <h2 className="text-lg font-semibold">Room Types</h2>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="btn btn-primary btn-sm"
                        >
                            <Plus className="w-4 h-4" />
                            Add Room Type
                        </button>
                    </div>

                    <div className="card-body">
                        {isLoading ? (
                            <div className="space-y-2">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                                ))}
                            </div>
                        ) : roomTypes.length === 0 ? (
                            <div className="text-center py-8">
                                <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                                <p className="text-slate-500">No room types yet</p>
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="btn btn-primary btn-sm mt-2"
                                >
                                    Add First Room Type
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {roomTypes.map((roomType: any) => (
                                    <div
                                        key={roomType.id}
                                        className="p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <h3 className="font-medium text-slate-900">{roomType.name}</h3>
                                                {roomType.description && (
                                                    <p className="text-sm text-slate-500 mt-1">{roomType.description}</p>
                                                )}
                                                <div className="flex items-center gap-4 mt-2">
                                                    <div>
                                                        <span className="text-xs text-slate-500">Default Rate</span>
                                                        <p className="text-lg font-semibold text-primary-600">
                                                            {formatCurrency(roomType.defaultRate)}/day
                                                        </p>
                                                    </div>
                                                    {roomType.markupPercentage > 0 && (
                                                        <div>
                                                            <span className="text-xs text-slate-500">Markup</span>
                                                            <p className="text-sm font-semibold text-amber-600">
                                                                +{roomType.markupPercentage}%
                                                            </p>
                                                        </div>
                                                    )}
                                                    {!roomType.isActive && (
                                                        <span className="badge badge-secondary">Inactive</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex gap-1">
                                                <button
                                                    onClick={() => handleEdit(roomType)}
                                                    className="p-2 hover:bg-slate-100 rounded"
                                                >
                                                    <Edit2 className="w-4 h-4 text-slate-500" />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (confirm('Delete this room type?')) {
                                                            deleteMutation.mutate(roomType.id);
                                                        }
                                                    }}
                                                    className="p-2 hover:bg-slate-100 rounded"
                                                >
                                                    <Trash2 className="w-4 h-4 text-error-500" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
                    <div className="card w-full max-w-md">
                        <div className="card-header flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                {editingRoomType ? 'Edit Room Type' : 'Add Room Type'}
                            </h2>
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    setEditingRoomType(null);
                                    reset({
                                        name: '',
                                        description: '',
                                        defaultRate: 0,
                                        displayOrder: 0,
                                    });
                                }}
                                className="p-1 hover:bg-slate-100 rounded"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="card-body space-y-4">
                                <div>
                                    <label className="label">Room Type Name *</label>
                                    <input
                                        type="text"
                                        className={`input ${errors.name ? 'input-error' : ''}`}
                                        placeholder="e.g., General Ward, ICU, Private Room"
                                        {...register('name')}
                                    />
                                    {errors.name && (
                                        <p className="error-text">{errors.name.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="label">Description</label>
                                    <textarea
                                        className="input"
                                        rows={2}
                                        placeholder="Optional description"
                                        {...register('description')}
                                    />
                                </div>

                                <div>
                                    <label className="label">Default Rate (per day) *</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className={`input ${errors.defaultRate ? 'input-error' : ''}`}
                                        placeholder="e.g., 2000"
                                        {...register('defaultRate', { valueAsNumber: true })}
                                    />
                                    {errors.defaultRate && (
                                        <p className="error-text">{errors.defaultRate.message}</p>
                                    )}
                                    <p className="text-xs text-slate-500 mt-1">
                                        This rate will be used by default for all procedures
                                    </p>
                                </div>

                                <div>
                                    <label className="label">Markup Percentage (%)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        max="100"
                                        className={`input ${errors.markupPercentage ? 'input-error' : ''}`}
                                        placeholder="e.g., 20 for 20% markup"
                                        {...register('markupPercentage', { valueAsNumber: true })}
                                    />
                                    {errors.markupPercentage && (
                                        <p className="error-text">{errors.markupPercentage.message}</p>
                                    )}
                                    <p className="text-xs text-slate-500 mt-1">
                                        Percentage markup applied to total estimate cost for this room type
                                    </p>
                                </div>

                                <div>
                                    <label className="label">Display Order</label>
                                    <input
                                        type="number"
                                        className="input"
                                        placeholder="0"
                                        {...register('displayOrder', { valueAsNumber: true })}
                                    />
                                </div>
                            </div>

                            <div className="card-footer flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        setEditingRoomType(null);
                                        reset({
                                            name: '',
                                            description: '',
                                            defaultRate: 0,
                                            displayOrder: 0,
                                        });
                                    }}
                                    className="btn btn-secondary btn-md"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={createMutation.isPending || updateMutation.isPending}
                                    className="btn btn-primary btn-md"
                                >
                                    {createMutation.isPending || updateMutation.isPending
                                        ? 'Saving...'
                                        : editingRoomType
                                            ? 'Update Room Type'
                                            : 'Add Room Type'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
